/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { DialogContext } from "../../components/Dialog";
import cn from "classnames";
import { PopupInput } from "./PopupInput";
import { addItem, removeItem } from "../Esign.common";
import { LayoutContext } from "../../components/layout";
import pdfContext from "../pdfContext";

export const CheckBoxItem = ({ item, addCst, removeCst, shouldShow, isLinked, pageNumber }) => {
  const prevent = useRef(true);
  const { headerDetails: userInfo } = useContext(LayoutContext);
  let ids = useRef(userInfo.reportStatements.map((item) => item.cstId));
  const { showDialog, hideDialog } = useContext(DialogContext);
  const [isChecked, setIsChecked] = useState(ids.current.includes(item.id));
  const [message, setMessage] = useState("");
  const [isShowForm, setIsShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const { offSet } = useContext(pdfContext);
  const { headerDetails, updateHeaderDetails } = useContext(LayoutContext);
  const [reportStateMentId, setReportStatementId] = useState(
    (userInfo.reportStatements[ids.current.indexOf(item.id)] || {}).id
  );

  const showStatus = (status, action, id) => {
    setIsLoading(false);
    if (action === "remove") {
      return removeCst(id);
    }
    setIsSuccess(status);
    setTimeout(() => {
      setIsSuccess(null);
      if (action === "add") {
        addCst(id);
      }
    }, 1500);
  };

  const savePageNumber = (number) => {
    let updatedObject;
    if (!headerDetails.currentPdfPage) {
      updatedObject = { ...headerDetails, ["currentPdfPage"]: number + offSet };
    } else {
      const headerDetailsCopy = { ...headerDetails };
      headerDetailsCopy.currentPdfPage = number + offSet;
      updatedObject = headerDetailsCopy;
    }
    updateHeaderDetails(updatedObject);
  };

  const updateLinkNumber = (id) => {
    updateHeaderDetails({
      ...headerDetails,
      reportStatements: headerDetails.reportStatements.map((elem) => {
        {
          if (elem.cstId === id) {
            return {
              ...elem,
              pdfLink: 0,
            };
          }
          return elem;
        }
      }),
    });
  };

  const updateData = async (isChecked) => {
    setIsLoading(true);
    try {
      let response;
      if (isChecked) {
        response = await addItem({ item, userInfo });
        showStatus(true, "add", item.id);
      } else {
        response = await removeItem({ item, userInfo });
        showStatus(true, "remove", item.id);
      }
      // when the response is remove we should not await response
      if (isChecked) {
        const json = await response.json();
        setReportStatementId(json.id);
      }
    } catch (err) {
      showStatus(false);
      prevent.current = true;
      setTimeout(() => {
        setIsChecked(!isChecked);
      }, 100);
    }
  };

  useEffect(() => {
    if (!prevent.current) updateData(isChecked);
    return () => {
      prevent.current = false;
    };
  }, [isChecked]);

  if (!item.conclusionStatement || item.conclusionStatement === "") {
    return null;
  }

  const handleUncheckWithComment = (e) => {
    e.preventDefault();
    if ((message && isChecked) || (isLinked && isChecked)) {
      showDialog({
        message: `<strong>"${item.conclusionStatement}"</strong> has comments and/or pdf linking. Do you want to proceed?`,
        buttons: [
          { label: "Cancel", onClick: () => hideDialog() },
          {
            label: "Proceed",
            type: "success",
            onClick: () => {
              hideDialog();
              setIsChecked(!isChecked);
              updateLinkNumber(item.id);
            },
          },
        ],
      });
    } else {
      setIsChecked(!isChecked);
      updateLinkNumber(item.id);
    }
  };

  return (
    <div hidden={!shouldShow}>
      <div
        style={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          position: "relative",
          flexWrap: "wrap",
          backgroundColor: "transparent",
        }}
        className="checkbox-container"
      >
        <label
          className="form-li"
          style={{
            flex: 1,
            marginRight: 10,
            marginBottom: 3,
            display: "flex",
            alignItems: "center",
          }}
          onClick={handleUncheckWithComment}
        >
          <input
            type="checkbox"
            className="chk"
            disabled={isLoading}
            checked={isChecked}
            onChange={() => null}
            id="statement-chkbox"
            data-testid={`statement-chkbox-${item.id}`}
          />
          <span className="chkmrk" />
          <span className="lbl-txt" id="conclusion-stat" style={{ display: "flex", flex: 1 }}>
            {item.conclusionStatement}
          </span>
        </label>
        {isLoading ? (
          <div
            className="saving-teal-ico icon-64"
            id="stat-save-icon"
            style={{ marginLeft: 5, marginRight: 15 }}
          />
        ) : null}
        {isSuccess !== null && !isLoading ? (
          <div
            className={cn({ "check-ico": isSuccess }, { "close-ico": !isSuccess }, "icon-64")}
            style={{ marginLeft: 5, marginRight: 5 }}
          />
        ) : null}
        {isLinked && isChecked ? (
          <a
            href="#"
            aria-label="link"
            style={{
              marginRight: 10,
              outline: "none",
              textDecoration: "none",
              color: !isShowForm ? "#555" : undefined,
            }}
            onClick={(e) => {
              e.preventDefault();
              savePageNumber(pageNumber);
            }}
          >
            <div className="icon-64 gray-link-ico" id="gray-pencil" />
          </a>
        ) : null}
        {isChecked ? (
          <a
            href="#"
            style={{
              outline: "none",
              textDecoration: "none",
              color: !isShowForm ? "#555" : undefined,
            }}
            onClick={(e) => {
              e.preventDefault();
              setIsShowForm(true);
            }}
          >
            <div className="icon-64 pencil-gray-icon" id="pencil-icon" />
          </a>
        ) : (
          <div style={{ width: 16, height: 18.4, marginRight: 5 }} />
        )}
      </div>
      <PopupInput
        reportStateMentId={reportStateMentId}
        isChecked={isChecked}
        onChangeMessage={(message) => setMessage(message)}
        isShowForm={isShowForm}
        setIsShowForm={setIsShowForm}
        item={item}
        pdfPage={pageNumber}
      />
    </div>
  );
};
